/**
 * ResizableVoicePanel — 2026-06-05 voice-area height drag handle.
 *
 * User feedback: "i want to be able to adjust the vertical height of the
 * voice chat area at the top - we just need to make it a moveable lower
 * div edge". The voice area (VoiceTabsStrip + VoiceControlsBar +
 * VoiceHistoryLog) is rendered at fixed intrinsic height in all 4
 * shells (AppShell, ModuleHostPane, KnowledgeShell, TerminalsShell).
 * This wrapper gives it an explicit pixel height + a drag handle at
 * the bottom edge that the user can grab to resize.
 *
 * Persistence: height is stored at `vibe-designer:voice-panel-height-px`
 * so it survives reload + applies across all 4 shells (single global
 * setting per the user's "the voice chat area at the top" phrasing).
 *
 * Bounds: clamped to [VOICE_MIN_HEIGHT_PX, 70% of viewport]. The
 * minimum keeps the tabs strip + record button visible; the max stops
 * the user from accidentally hiding the rest of the shell.
 *
 * Default: leaves height unconstrained (CSS `auto`) until the user
 * drags. First-drag bootstraps from the panel's measured offsetHeight
 * so the resize starts from the current rendered position rather than
 * snapping to an arbitrary default.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { VoicePanel } from './VoicePanel';
// Persistence + bounds helpers live in a sibling non-component module so this
// file only exports the component (react-refresh/only-export-components).
import {
  VOICE_MIN_HEIGHT_PX,
  maxHeightPx,
  readVoicePanelHeight,
  writeVoicePanelHeight,
} from './voicePanelHeight';

export interface ResizableVoicePanelProps {
  /** Hook for tests. */
  testidPrefix?: string;
}

export function ResizableVoicePanel({
  testidPrefix = 'voice-panel-resize',
}: ResizableVoicePanelProps) {
  const [heightPx, setHeightPx] = useState<number | null>(
    () => readVoicePanelHeight(),
  );
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ startY: number; startHeight: number } | null>(null);
  // Tracks the live height during a drag so pointerup can commit it to
  // localStorage without depending on a React rerender having flushed
  // the latest state into the callback closure. Without this, fast
  // drags (pointerdown→up within a single React batch) commit the
  // pre-drag height to localStorage instead of the new one.
  const liveHeightRef = useRef<number | null>(heightPx);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const startY = e.clientY;
    const measured = wrapperRef.current?.getBoundingClientRect().height ?? VOICE_MIN_HEIGHT_PX;
    const startHeight = liveHeightRef.current ?? Math.round(measured);
    dragStateRef.current = { startY, startHeight };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (state === null) return;
    const delta = e.clientY - state.startY;
    const proposed = state.startHeight + delta;
    const clamped = Math.max(VOICE_MIN_HEIGHT_PX, Math.min(maxHeightPx(), proposed));
    liveHeightRef.current = clamped;
    setHeightPx(clamped);
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStateRef.current === null) return;
    dragStateRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // Pointer was never captured on this element — fine.
    }
    // Read from the ref (not state) so a single fast drag commits the
    // final post-drag height rather than the stale pre-drag closure.
    if (liveHeightRef.current !== null) {
      writeVoicePanelHeight(liveHeightRef.current);
    }
  }, []);

  // Double-click on the handle resets to intrinsic (auto) — the
  // "I dragged this somewhere weird and want default back" affordance.
  const onDoubleClick = useCallback(() => {
    liveHeightRef.current = null;
    setHeightPx(null);
    writeVoicePanelHeight(null);
  }, []);

  // If the window resizes below the max bound, clamp current height so
  // the panel doesn't end up wider than the viewport allows.
  useEffect(() => {
    if (heightPx === null) return;
    const onResize = () => {
      const max = maxHeightPx();
      if (heightPx > max) {
        const clamped = Math.max(VOICE_MIN_HEIGHT_PX, max);
        setHeightPx(clamped);
        writeVoicePanelHeight(clamped);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [heightPx]);

  // The voice area is a flex COLUMN that CLIPS (overflow:hidden) — the tabs
  // strip + controls bar + tool strip stay pinned and ONLY the transcript log
  // (VoiceHistoryLog, flex-1 min-h-0 overflow-y-auto) scrolls. Pre-2026-07-03
  // this wrapper used `overflow:'auto'` on the WHOLE panel, so dragging the
  // area scrolled the controls out of view AND added a second scrollbar on top
  // of the log's own. Now there is exactly ONE vertical scroll (the transcript)
  // and the controls never scroll. `null` (no persisted drag) leaves `height`
  // unset (intrinsic) but still clips via a maxHeight so the log bounds + scrolls.
  const wrapperStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    flexShrink: 0,
    ...(heightPx === null ? { maxHeight: '18rem' } : { height: `${heightPx}px` }),
  };

  return (
    <>
      <div
        ref={wrapperRef}
        data-testid={testidPrefix}
        style={wrapperStyle}
      >
        <VoicePanel />
      </div>
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize voice panel — drag up/down; double-click to reset"
        tabIndex={0}
        data-testid={`${testidPrefix}-handle`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        // Tailwind classes mirror the existing voice-area chrome palette
        // — thin horizontal bar that's visible enough to grab but doesn't
        // visually steal attention from the briefing room below.
        className="relative h-1.5 w-full cursor-row-resize bg-vibe-bg-tertiary hover:bg-vibe-accent/40 transition-colors flex-shrink-0 select-none touch-none"
      >
        {/* Inner grab-dots for visual affordance. */}
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-vibe-text-secondary text-[10px] leading-none pointer-events-none"
        >
          ⋯
        </span>
      </div>
    </>
  );
}

export default ResizableVoicePanel;
